export default function Hero() {
  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/231565e2b380092a04ceffc2a9a6d4a0.jpg"
          alt="Luxury home interior with HVAC comfort"
          className="w-full h-full object-cover object-top"
        />
        {/* Left-side gradient only — keeps right side of image fully visible */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/88 via-[#0a1628]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/60 via-transparent to-transparent" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[300px] bg-[#00d4ff]/5 blur-[100px] rounded-full pointer-events-none" />
      </div>

      {/* Animated airflow lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-[#00d4ff]/12 to-transparent"
            style={{
              top: `${25 + i * 11}%`,
              left: '-100%',
              width: '200%',
              animation: `airflow ${4 + i * 0.6}s linear infinite`,
              animationDelay: `${i * 1.1}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-16">
        <div className="max-w-[720px]">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#00d4ff]/15 border border-[#00d4ff]/30 rounded-full px-4 py-1.5 mb-8">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-[#00d4ff] text-xs" />
            </div>
            <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Southern NC&apos;s Most Trusted HVAC Service</span>
          </div>

          {/* H1 */}
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 2px 24px rgba(0,0,0,0.7), 0 1px 4px rgba(0,0,0,0.9)' }}
          >
            Reliable HVAC
            <br />
            Solutions Built for
            <br />
            <span className="text-[#00d4ff]" style={{ textShadow: '0 2px 20px rgba(0,212,255,0.4), 0 1px 4px rgba(0,0,0,0.8)' }}>Year-Round Comfort</span>
          </h1>

          {/* Sub */}
          <p
            className="text-lg md:text-xl text-white leading-relaxed max-w-[560px] font-semibold mb-4 px-4 py-3 rounded-xl"
            style={{ textShadow: '0 1px 8px rgba(0,0,0,0.9)', background: 'rgba(10,22,40,0.45)', backdropFilter: 'blur(6px)' }}
          >
            Heating, cooling, and air quality services delivered with precision, efficiency, and long-term reliability.
          </p>

          {/* Available 24/7 */}
          <div className="flex items-center gap-2 mb-10">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-green-300 text-sm font-bold" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>Available 24/7 — Fast Response Times</span>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
              className="bg-[#00d4ff] hover:bg-[#00b8d9] text-[#0a1628] font-bold text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
            >
              Request Service Now
            </a>
            <a
              href="tel:9193489399"
              className="flex items-center justify-center gap-2 bg-white/15 backdrop-blur-sm border-2 border-white/60 hover:border-white hover:bg-white/25 text-white font-bold text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill" />
              </div>
              Call (919) 348-9399
            </a>
          </div>

          {/* Trust micro-signals */}
          <div className="flex flex-wrap items-center gap-6 mt-10">
            {['Licensed & Insured', '100% Financing Available', 'Free Quotes'].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-[#00d4ff] text-sm" />
                </div>
                <span className="text-white/80 text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <button
          onClick={() => handleNav('#trust-bar')}
          className="flex flex-col items-center gap-1 text-white/50 hover:text-[#00d4ff] transition-colors duration-200 cursor-pointer"
          aria-label="Scroll down"
        >
          <div className="w-5 h-5 flex items-center justify-center animate-bounce">
            <i className="ri-arrow-down-line text-lg" />
          </div>
        </button>
      </div>

      <style>{`
        @keyframes airflow {
          0% { transform: translateX(-50%); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateX(50%); opacity: 0; }
        }
      `}</style>
    </section>
  );
}
